import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import api, { API } from "@/lib/api";
import { motion } from "framer-motion";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MapPin } from "lucide-react";

const fmt = (n) => "$" + Number(n || 0).toLocaleString();
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export default function PropertyDetail() {
  const { id } = useParams();
  const [h, setH] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/houses/${id}`).then((r) => setH(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F9F8F6]">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="property-not-found">
          <h1 className="font-display font-bold text-2xl text-[#0A2E26]">Property not found</h1>
          <Link to="/properties"><Button className="mt-6 bg-[#0A2E26] text-white rounded-md">Back to portfolio</Button></Link>
        </div>
      </div>
    );
  }
  if (!h) {
    return (
      <div className="min-h-screen bg-[#F9F8F6]">
        <Navbar />
        <p className="max-w-7xl mx-auto px-6 py-24 text-[#5C5C5C]">Loading property…</p>
      </div>
    );
  }

  const profit = (h.sale_price || 0) - (h.purchase_price || 0) - (h.rehab_cost || 0);
  const photoEntries = (h.photos || []).map((p) => ({ ...p, kind: "photo" }));
  const updateEntries = (h.updates || []).map((u) => ({ ...u, kind: "update" }));
  const entries = [...photoEntries, ...updateEntries].sort(
    (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
  );
  const photos = photoEntries;
  const hero = h.image || (photos[0] && `${API}/files/${photos[0].storage_path}`) ||
    "https://images.unsplash.com/photo-1721244654394-36a7bc2da288?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(h.address)}&output=embed`;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-8" data-testid="property-detail">
        <Link to="/properties" data-testid="back-to-portfolio" className="inline-flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#0A2E26] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to portfolio
        </Link>

        <div className="mt-5 grid lg:grid-cols-3 gap-8">
          {/* Left: visuals */}
          <div className="lg:col-span-2">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl overflow-hidden border border-[#E5E5E5]">
              <img src={hero} alt={h.name} className="w-full h-[380px] object-cover" />
            </motion.div>

            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0A2E26] tracking-tight">{h.name}</h1>
              <StatusBadge status={h.status} />
            </div>
            <p className="text-[#5C5C5C] mt-1 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {h.address} · Cycle #{h.cycle}</p>

            {h.status === "rehab" && (
              <div className="mt-5 max-w-md">
                <div className="flex justify-between text-xs text-[#5C5C5C] mb-1">
                  <span>Rehab progress</span><span>{h.rehab_progress || 0}%</span>
                </div>
                <Progress value={h.rehab_progress || 0} className="h-2" />
              </div>
            )}

            {h.notes && <p className="mt-5 text-[#5C5C5C] leading-relaxed max-w-2xl">{h.notes}</p>}

            {/* Rehab journal — photos + CMT updates, chronological */}
            <h2 className="font-display font-bold text-2xl text-[#0A2E26] mt-10">Rehab journal</h2>
            {entries.length === 0 ? (
              <p className="mt-3 text-[#5C5C5C]" data-testid="no-photos">No journal entries yet.</p>
            ) : (
              <div className="mt-6 relative pl-8" data-testid="detail-timeline">
                <div className="absolute left-[7px] top-2 bottom-2 w-px bg-[#E5E5E5]" />
                {entries.map((e) => (
                  e.kind === "photo" ? (
                    <div key={e.id} className="relative mb-8 last:mb-0" data-testid={`journal-entry-${e.id}`}>
                      <span className="absolute -left-8 top-1.5 w-3.5 h-3.5 rounded-full bg-[#C49A45] ring-4 ring-[#F9F8F6]" />
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs font-bold uppercase tracking-wide text-[#0A2E26] bg-[#0A2E26]/5 px-2 py-0.5 rounded capitalize">{e.label}</span>
                        <span className="text-xs text-[#5C5C5C]" data-testid={`journal-date-${e.id}`}>{fmtDate(e.created_at)}</span>
                      </div>
                      <div className="mt-3 rounded-xl overflow-hidden border border-[#E5E5E5] bg-white max-w-xl">
                        <img src={`${API}/files/${e.storage_path}`} alt={e.label} className="w-full h-72 object-cover" />
                        {e.caption && (
                          <p className="text-sm text-[#1A1A1A] px-4 py-3 leading-relaxed" data-testid={`journal-caption-${e.id}`}>{e.caption}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div key={e.id} className="relative mb-8 last:mb-0" data-testid={`journal-update-${e.id}`}>
                      <span className="absolute -left-8 top-1.5 w-3.5 h-3.5 rounded-full bg-[#0A2E26] ring-4 ring-[#F9F8F6]" />
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs font-bold uppercase tracking-wide text-white bg-[#0A2E26] px-2 py-0.5 rounded">CMT Update</span>
                        <span className="text-xs text-[#5C5C5C]">{fmtDate(e.created_at)}</span>
                      </div>
                      <div className="mt-3 rounded-xl border border-[#E5E5E5] bg-white max-w-xl px-4 py-3">
                        <p className="text-sm text-[#1A1A1A] leading-relaxed">{e.note}</p>
                        <p className="text-xs text-[#5C5C5C] mt-2">— {e.author || "CMT Home and Property Solutions LLC"}</p>
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}

            {/* Map */}
            <h2 className="font-display font-bold text-2xl text-[#0A2E26] mt-10">Location</h2>
            <div className="mt-4 rounded-xl overflow-hidden border border-[#E5E5E5]" data-testid="property-map">
              <iframe title="map" src={mapSrc} width="100%" height="320" loading="lazy" style={{ border: 0 }} />
            </div>
          </div>

          {/* Right: financials */}
          <div>
            <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 sticky top-20" data-testid="property-financials">
              <h3 className="font-display font-bold text-lg text-[#0A2E26]">Financials</h3>
              <div className="mt-4 space-y-3 text-sm">
                <Row label="Purchase price" value={fmt(h.purchase_price)} />
                <Row label="Rehab cost" value={fmt(h.rehab_cost)} />
                <Row label="Total invested" value={fmt((h.purchase_price || 0) + (h.rehab_cost || 0))} bold />
                {h.status === "sold" && (
                  <>
                    <div className="h-px bg-[#E5E5E5] my-2" />
                    <Row label="Sold for" value={fmt(h.sale_price)} />
                    <Row label="Realized profit" value={fmt(profit)} accent="#059669" bold />
                  </>
                )}
                {(h.status === "rental" || h.retained) && (
                  <div className="mt-3 text-xs text-[#059669] bg-[#E7F6EF] rounded-md px-3 py-2 font-medium">
                    Retained as a long-term rental for recurring income.
                  </div>
                )}
              </div>
              <div className="mt-5 pt-4 border-t border-[#E5E5E5]" data-testid="managed-by">
                <p className="text-xs uppercase tracking-wide text-[#5C5C5C] font-semibold">Managed by</p>
                <p className="text-sm font-medium text-[#0A2E26] mt-1">CMT Home and Property Solutions LLC</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, accent }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[#5C5C5C]">{label}</span>
      <span className={bold ? "font-semibold" : "font-medium"} style={{ color: accent || "#0A2E26" }}>{value}</span>
    </div>
  );
}
